import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, mediaUrl } from "@/lib/api";
import { Surface, Pill, AButton, ConditionDots } from "@/components/ios";
import { SignaturePad } from "@/components/SignaturePad";
import { Building2, Check, X, Fingerprint, Loader2, Camera, CheckCircle2 } from "lucide-react";
import { compressImage } from "@/lib/image";
import { toast } from "sonner";
import { motion } from "framer-motion";

const SEV_TONE = { none: "neutral", minor: "warning", moderate: "warning", major: "danger" };

export default function TenantPortal() {
  const { token } = useParams();
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState("");
  const [responses, setResponses] = useState({}); // item_key -> {agreement, comment, media_ids}
  const [uploads, setUploads] = useState({}); // item_key -> [signed_url]
  const [submitting, setSubmitting] = useState(false);
  const [sigName, setSigName] = useState("");
  const [sig, setSig] = useState(null);
  const [signing, setSigning] = useState(false);

  const load = () => axios.get(`${API}/portal/${token}`).then(({ data }) => { setRep(data); if (!sigName) setSigName(data.tenancy?.tenants?.[0]?.name || ""); })
    .catch(() => setErr("This link is invalid or has expired."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const setResp = (key, patch) => setResponses((r) => ({ ...r, [key]: { ...(r[key] || { agreement: "agree" }), ...patch } }));
  const addMedia = (key, id, url) => {
    setResponses((r) => ({ ...r, [key]: { ...(r[key] || { agreement: "agree" }), media_ids: [...((r[key]?.media_ids) || []), id] } }));
    setUploads((u) => ({ ...u, [key]: [...(u[key] || []), url] }));
  };

  const onItemPhoto = (key) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const compressed = await compressImage(reader.result);
        const { data } = await axios.post(`${API}/portal/${token}/media`, { data_url: compressed, item_key: key });
        addMedia(key, data.id, data.signed_url);
        toast.success("Photo added");
      } catch (er) { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/${token}/respond`, { items: responses });
      toast.success("Your response has been recorded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  const signAccept = async () => {
    if (!sigName.trim() || !sig) return toast.error("Enter your name and sign");
    setSigning(true);
    try {
      await axios.post(`${API}/portal/${token}/sign`, { name: sigName.trim(), role: "tenant", signature_image: sig });
      toast.success("Report accepted and signed");
      load();
    } catch (e) { toast.error("Could not sign"); }
    finally { setSigning(false); }
  };

  if (err) return <Centered>{err}</Centered>;
  if (!rep) return <Centered><Loader2 className="animate-spin accent-text" /></Centered>;

  const responded = rep.status === "tenant_responded" || rep.status === "final";
  const tenant = rep.tenancy?.tenants?.[0];
  const tenantSigned = (rep.signatures || []).some((s) => s.party === "tenant");
  const inp = { background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <header className="glass sticky top-0 z-20 border-b px-5 py-4 flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
        <div className="h-9 w-9 rounded-xl grid place-items-center text-white" style={{ background: "var(--accent)" }}><Building2 size={18} /></div>
        <div><div className="font-semibold" style={{ color: "var(--text)" }}>Condition Report</div><div className="text-xs text-secondary">{rep.property?.address}, {rep.property?.suburb}</div></div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>{rep.schema?.type_label}</h1>
            <p className="text-sm text-secondary">Hi {tenant?.name || "there"} — review each item, add your own photos, then sign.</p>
          </div>
          <Pill tone={responded ? "success" : "warning"}>{responded ? "Response recorded" : "Awaiting response"}</Pill>
        </div>

        {rep.response_deadline && !responded && (
          <Surface className="p-4 mb-4"><p className="text-sm" style={{ color: "var(--text)" }}>You have until <b>{new Date(rep.response_deadline).toLocaleDateString("en-AU")}</b> to respond. Items with no response are taken as agreed.</p></Surface>
        )}

        {rep.report_json.sections.map((sec) => (
          <div key={sec.key} className="mb-4">
            <div className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-secondary">{sec.title}</div>
            <Surface className="overflow-hidden"><div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {sec.items.map((it) => {
                const r = responses[it.item_key];
                return (
                  <div key={it.item_key} className="p-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium" style={{ color: "var(--text)" }}>{it.label}</span>
                      <ConditionDots condition={it.condition} />
                      {it.severity !== "none" && <Pill tone={SEV_TONE[it.severity]}>{it.severity}</Pill>}
                    </div>
                    <p className="text-sm mt-1" style={{ color: it.comment === "Not sighted." ? "var(--text-secondary)" : "var(--text)" }}>{it.comment}</p>

                    {(uploads[it.item_key] || []).length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {uploads[it.item_key].map((u, i) => <img key={i} src={mediaUrl(u)} alt="tenant" className="h-16 w-16 object-cover rounded-lg" />)}
                      </div>
                    )}

                    {!responded && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <button data-testid={`agree-${it.item_key}`} onClick={() => setResp(it.item_key, { agreement: "agree" })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: r?.agreement === "agree" ? "var(--success)" : "var(--surface-2)", color: r?.agreement === "agree" ? "#fff" : "var(--text-secondary)" }}><Check size={12} /> Agree</button>
                        <button data-testid={`disagree-${it.item_key}`} onClick={() => setResp(it.item_key, { agreement: "disagree" })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: r?.agreement === "disagree" ? "var(--danger)" : "var(--surface-2)", color: r?.agreement === "disagree" ? "#fff" : "var(--text-secondary)" }}><X size={12} /> Disagree</button>
                        <label data-testid={`photo-${it.item_key}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                          <Camera size={12} /> Photo
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onItemPhoto(it.item_key)} />
                        </label>
                        {r?.agreement === "disagree" && (
                          <input value={r.comment || ""} onChange={(e) => setResp(it.item_key, { comment: e.target.value })} placeholder="Add your comment…"
                            className="flex-1 min-w-[140px] text-sm rounded-lg px-3 py-1.5 outline-none border" style={inp} />
                        )}
                      </div>
                    )}
                    {responded && rep.tenant_response?.items?.[it.item_key] && (
                      <div className="mt-2 flex items-center gap-2">
                        <Pill tone={rep.tenant_response.items[it.item_key].agreement === "agree" ? "success" : "danger"}>{rep.tenant_response.items[it.item_key].agreement}</Pill>
                        {rep.tenant_response.items[it.item_key].comment && <span className="text-xs text-secondary">{rep.tenant_response.items[it.item_key].comment}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div></Surface>
          </div>
        ))}

        {!responded && (
          <motion.div whileTap={{ scale: 0.99 }} className="mb-4">
            <AButton testid="submit-response" className="w-full !py-3.5" onClick={submit} disabled={submitting}>
              {submitting ? "Submitting…" : `Submit response (${Object.keys(responses).length} items marked)`}
            </AButton>
          </motion.div>
        )}

        {/* Digital signature — acceptance of the report */}
        {tenantSigned ? (
          <Surface className="p-6 text-center">
            <CheckCircle2 size={36} className="accent-text mx-auto" />
            <div className="font-semibold mt-2" style={{ color: "var(--text)" }}>You've signed this report</div>
            <div className="text-sm text-secondary">Recorded {new Date((rep.signatures.find((s) => s.party === "tenant") || {}).at).toLocaleString("en-AU")}</div>
          </Surface>
        ) : (
          <Surface className="p-5">
            <div className="font-medium mb-1" style={{ color: "var(--text)" }}>Sign to acknowledge this condition report</div>
            <p className="text-xs text-secondary mb-3">Your signature records that you've reviewed the report. It doesn't change the agent's findings — your agreements/disputes above are stored separately.</p>
            <input value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder="Your full name" data-testid="tenant-sign-name"
              className="w-full text-sm rounded-lg px-3 py-2 outline-none border mb-2" style={inp} />
            <SignaturePad testid="tenant-signature" onChange={setSig} />
            <AButton testid="tenant-sign-btn" className="w-full mt-3" onClick={signAccept} disabled={signing}>{signing ? "Saving…" : "Sign & acknowledge"}</AButton>
          </Surface>
        )}

        <p className="text-xs text-secondary text-center mt-6 flex items-center justify-center gap-1"><Fingerprint size={11} /> Report version {rep.version_hash?.slice(0, 12)} · Your response and signature are stored as separate structured records and never edit the agent's report.</p>
      </main>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen grid place-items-center text-secondary" style={{ background: "var(--bg)" }}>{children}</div>;
}
